import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Client, IFrame, IMessage, StompConfig, StompSubscription } from '@stomp/stompjs';
import { Observable, Subject } from 'rxjs';
import SockJS from 'sockjs-client';
import { AppService } from '../../app.service';

/**
 * Angular 12 replacement for the legacy AngularJS `MessageService` factory
 * (ui/app/GlobalServices.js) built on `stompjs@2.x` + `sockjs-client`.
 *
 * Key differences versus legacy:
 *  - Uses the maintained fork `@stomp/stompjs` (native TypeScript, auto-reconnect,
 *    compatible with modern Angular / Webpack bundlers).
 *  - Exposes both a callback-based API (drop-in for the legacy surface) and an
 *    RxJS `Observable` stream per (endpoint, channel).
 *  - Manages subscription request queue so callers can subscribe before the
 *    STOMP session is activated.
 *  - Caches one client per endpoint to prevent duplicate connections.
 */

type ConnectionState = 'connecting' | 'connected' | 'disconnected';

interface ManagedSubscription {
  endPoint: string;
  channel: string;
  subject: Subject<IMessage>;
  stompSubscription?: StompSubscription;
  callbacks: Array<(message: IMessage) => void>;
}

interface ManagedClient {
  endPoint: string;
  client: Client;
  state: ConnectionState;
  subscriptions: ManagedSubscription[];
  pendingCallbacks: Array<(client: ManagedClient) => void>;
}

interface QueuedSubscriptionRequest {
  endPoint: string;
  channel: string;
  callback: (message: IMessage) => void;
}

/**
 * Legacy-compatible shape returned from `getStompClient()`.
 * Matches the object structure consumed by existing controllers that were
 * migrated from AngularJS (vm.cacheStore.listener, etc.).
 */
export interface LegacyStompClientHandle {
  endPoint: string;
  client: Client;
  state: ConnectionState;
  subscriptions: ManagedSubscription[];
}

@Injectable({ providedIn: 'root' })
export class StompMessageService implements OnDestroy {

  private clients: ManagedClient[] = [];
  private queue: QueuedSubscriptionRequest[] = [];
  private static readonly DEFAULT_RECONNECT_DELAY_MS = 5000;
  private static readonly DEFAULT_HEARTBEAT_MS = 10000;

  constructor(private zone: NgZone, private appService: AppService) {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.unsubscribeAllChannels());
    }
  }

  /**
   * Look up the managed client for an endpoint (or undefined).
   * Matches legacy `MessageService.getStompClient(endPoint)`.
   */
  getStompClient(endPoint: string): LegacyStompClientHandle | undefined {
    return this.clients.find(c => c.endPoint === endPoint);
  }

  /**
   * Open (or reuse) a STOMP-over-SockJS connection to the given endpoint.
   * `onConnected` is invoked once the session is activated.
   *
   * Matches legacy `MessageService.establishConnection(endPoint, callback)`.
   */
  establishConnection(
    endPoint: string,
    onConnected?: (client: LegacyStompClientHandle) => void
  ): LegacyStompClientHandle {
    let managed = this.clients.find(c => c.endPoint === endPoint);
    if (managed && managed.state === 'connected') {
      if (onConnected) { onConnected(managed); }
      return managed;
    }

    if (managed && managed.state === 'connecting') {
      if (onConnected) { managed.pendingCallbacks.push(onConnected); }
      return managed;
    }

    const ssoId = this.appService?.USER_SETTINGS?.user?.sso_id ?? '';
    const socketUrl = `${endPoint}${endPoint.includes('?') ? '&' : '?'}georaclehrid=${encodeURIComponent(ssoId)}`;

    const config: StompConfig = {
      webSocketFactory: () => new SockJS(socketUrl) as any,
      reconnectDelay: StompMessageService.DEFAULT_RECONNECT_DELAY_MS,
      heartbeatIncoming: StompMessageService.DEFAULT_HEARTBEAT_MS,
      heartbeatOutgoing: StompMessageService.DEFAULT_HEARTBEAT_MS,
      // Silence chatty debug output by default; swap for console.log when diagnosing.
      debug: () => { /* no-op */ }
    };

    const client = new Client(config);

    const newManaged: ManagedClient = managed ?? {
      endPoint,
      client,
      state: 'connecting',
      subscriptions: [],
      pendingCallbacks: onConnected ? [onConnected] : []
    };

    if (!managed) {
      this.clients.push(newManaged);
    } else {
      managed.client = client;
      managed.state = 'connecting';
      if (onConnected) { managed.pendingCallbacks.push(onConnected); }
    }

    client.onConnect = (_frame: IFrame) => {
      // Run back inside Angular's zone so downstream change detection fires.
      this.zone.run(() => {
        newManaged.state = 'connected';
        this.flushQueue(newManaged);

        // Re-attach any pre-existing channel subjects after reconnect.
        newManaged.subscriptions.forEach(sub => {
          if (!sub.stompSubscription) {
            sub.stompSubscription = client.subscribe(sub.channel, (message: IMessage) => {
              this.dispatch(sub, message);
            });
          }
        });

        const pending = newManaged.pendingCallbacks.splice(0);
        pending.forEach(cb => {
          try { cb(newManaged); } catch (err) { console.error('[StompMessageService] onConnect callback error', err); }
        });
      });
    };

    client.onStompError = (frame: IFrame) => {
      console.error('[StompMessageService] broker error:', frame.headers['message'], frame.body);
    };

    client.onWebSocketClose = () => {
      this.zone.run(() => { newManaged.state = 'disconnected'; });
    };

    client.onDisconnect = () => {
      this.zone.run(() => { newManaged.state = 'disconnected'; });
    };

    client.activate();
    return newManaged;
  }

  /**
   * Subscribe to a channel with a callback.
   * If the connection is still being established, the request is queued and
   * fulfilled after `onConnect`.
   *
   * Matches legacy `subscribeToChannelWithCallback(endPoint, channel, callback, client?)`.
   */
  subscribeToChannelWithCallback(
    endPoint: string,
    channel: string,
    callback: (message: IMessage) => void,
    handle?: LegacyStompClientHandle
  ): void {
    const managed = handle
      ? this.clients.find(c => c === handle || c.endPoint === handle.endPoint)
      : this.clients.find(c => c.endPoint === endPoint);

    if (!managed) {
      console.warn('[StompMessageService] subscribe called before establishConnection; queuing');
      this.queue.push({ endPoint, channel, callback });
      return;
    }

    if (managed.state === 'disconnected') {
      console.warn('[StompMessageService] subscribe called on disconnected client; queuing');
      this.queue.push({ endPoint, channel, callback });
      return;
    }

    const existing = managed.subscriptions.find(s => s.channel === channel);
    if (existing) {
      if (!existing.callbacks.includes(callback)) { existing.callbacks.push(callback); }
      return;
    }

    const subject = new Subject<IMessage>();
    const sub: ManagedSubscription = {
      endPoint: managed.endPoint,
      channel,
      subject,
      callbacks: [callback]
    };

    if (managed.state === 'connected') {
      sub.stompSubscription = managed.client.subscribe(channel, (message: IMessage) => this.dispatch(sub, message));
    }
    managed.subscriptions.push(sub);
  }

  /**
   * Modern observable API — preferred for new Angular components.
   * Returns a stream of `IMessage` payloads for the given (endpoint, channel).
   */
  watch(endPoint: string, channel: string): Observable<IMessage> {
    let managed = this.clients.find(c => c.endPoint === endPoint);
    if (!managed) {
      managed = this.establishConnection(endPoint) as ManagedClient;
    }

    let sub = managed.subscriptions.find(s => s.channel === channel);
    if (!sub) {
      const subject = new Subject<IMessage>();
      sub = {
        endPoint: managed.endPoint,
        channel,
        subject,
        callbacks: []
      };
      if (managed.state === 'connected') {
        const currentSub = sub;
        currentSub.stompSubscription = managed.client.subscribe(channel, (message: IMessage) => this.dispatch(currentSub, message));
      }
      managed.subscriptions.push(sub);
    }

    return sub.subject.asObservable();
  }

  /**
   * Publish to a channel. `body` is serialised to string automatically.
   */
  sendMessageToChannel(endPoint: string, destination: string, body: any): void {
    const managed = this.clients.find(c => c.endPoint === endPoint);
    if (!managed || managed.state !== 'connected') {
      console.warn('[StompMessageService] sendMessageToChannel called before connection ready');
      return;
    }
    const payload = typeof body === 'string' ? body : JSON.stringify(body ?? {});
    managed.client.publish({ destination, body: payload });
  }

  /**
   * Remove a single channel subscription for a given client.
   */
  unsubscribeChannel(handleOrEndpoint: LegacyStompClientHandle | string, channel: string): void {
    const endPoint = typeof handleOrEndpoint === 'string' ? handleOrEndpoint : handleOrEndpoint.endPoint;
    const managed = this.clients.find(c => c.endPoint === endPoint);
    if (!managed) { return; }

    const idx = managed.subscriptions.findIndex(s => s.channel === channel);
    if (idx === -1) { return; }

    const sub = managed.subscriptions[idx];
    try { sub.stompSubscription?.unsubscribe(); } catch { /* ignore */ }
    try { sub.subject.complete(); } catch { /* ignore */ }
    managed.subscriptions.splice(idx, 1);
  }

  /**
   * Unsubscribe every channel on every client and deactivate all clients.
   * Safe to call on `beforeunload` and `ngOnDestroy`.
   */
  unsubscribeAllChannels(): void {
    this.clients.forEach(managed => {
      managed.subscriptions.forEach(sub => {
        try { sub.stompSubscription?.unsubscribe(); } catch { /* ignore */ }
        try { sub.subject.complete(); } catch { /* ignore */ }
      });
      managed.subscriptions = [];
      try { managed.client.deactivate().catch(() => { /* ignore */ }); } catch { /* ignore */ }
    });
    this.clients = [];
    this.queue = [];
  }

  ngOnDestroy(): void {
    this.unsubscribeAllChannels();
  }

  private flushQueue(managed: ManagedClient): void {
    const forThisEndpoint = this.queue.filter(q => q.endPoint === managed.endPoint);
    this.queue = this.queue.filter(q => q.endPoint !== managed.endPoint);
    forThisEndpoint.forEach(q =>
      this.subscribeToChannelWithCallback(q.endPoint, q.channel, q.callback, managed)
    );
  }

  private dispatch(sub: ManagedSubscription, message: IMessage): void {
    this.zone.run(() => {
      sub.callbacks.forEach(cb => {
        try { cb(message); } catch (err) { console.error('[StompMessageService] callback error', err); }
      });
      sub.subject.next(message);
    });
  }
}
